import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PROJECT_STATUS,
  PROJECT_STATUS_LABELS,
  type ProjectInput,
  type ProjectStatus,
} from '@stromo/shared';
import {
  useCreateProject,
  useDeleteProject,
  usePatchProject,
  useProjects,
  useUpdateProject,
  type ProjectFilters,
  type ProjectListItem,
} from '../lib/projects';
import { useUsersLookup } from '../lib/lookups';
import { useAuthStore } from '../lib/authStore';
import { apiErrorMessage } from '../lib/api';
import { formatDate } from '../lib/format';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ProjectForm } from '../components/ProjectForm';
import { StatusBadge } from '../components/StatusBadge';
import { api } from '../lib/api';

async function downloadOverviewPdf(filters: ProjectFilters) {
  const response = await api.get('/export/projects/pdf', {
    params: filters,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Stromo_Projektuebersicht_${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type SortKey =
  | 'order_number'
  | 'title'
  | 'status'
  | 'start_date'
  | 'end_date'
  | 'updated_at';

export function ProjectsPage() {
  const { user } = useAuthStore();
  const canCreate = user?.role === 'admin' || user?.role === 'project_manager';
  const canDelete = user?.role === 'admin';
  // Bearbeiten / Archivieren — gleich wie Server-Guard (admin + PL).
  const canEdit = user?.role === 'admin' || user?.role === 'project_manager';

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [pmFilter, setPmFilter] = useState<number | ''>('');
  const [showArchived, setShowArchived] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('updated_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ProjectListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProjectListItem | null>(null);

  const filters: ProjectFilters = useMemo(
    () => ({
      q: query.trim() || undefined,
      status: status || undefined,
      project_manager_id: pmFilter || undefined,
      is_archived: showArchived ? undefined : false,
      sort: sortKey,
      direction: sortDir,
    }),
    [query, status, pmFilter, showArchived, sortKey, sortDir],
  );

  const projects = useProjects(filters);
  const users = useUsersLookup();
  const createMut = useCreateProject();
  const updateMut = useUpdateProject(editTarget?.id ?? 0);
  const patchMut = usePatchProject();
  const deleteMut = useDeleteProject();

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleCreate = (input: ProjectInput) => {
    createMut.mutate(input, {
      onSuccess: () => {
        setCreateOpen(false);
        createMut.reset();
      },
    });
  };

  const handleUpdate = (input: ProjectInput) => {
    if (!editTarget) return;
    updateMut.mutate(input, {
      onSuccess: () => {
        setEditTarget(null);
        updateMut.reset();
      },
    });
  };

  const handleArchiveToggle = (project: ProjectListItem) => {
    patchMut.mutate({ id: project.id, patch: { is_archived: !project.is_archived } });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">Projekte</div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => downloadOverviewPdf(filters)}
          >
            PDF-Export
          </button>
          {canCreate && (
            <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              + Neues Projekt
            </button>
          )}
        </div>
      </header>

      <div className="content">
        <div className="filter-bar" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
          <div className="search-wrap" style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Auftragsnummer, Projekt, Notiz …"
              style={{ width: '100%' }}
            />
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus | '')}>
            <option value="">Alle Status</option>
            {PROJECT_STATUS.map((s) => (
              <option key={s} value={s}>
                {PROJECT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>

          <select
            value={pmFilter}
            onChange={(e) => setPmFilter(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Alle Projektleiter</option>
            {users.data
              ?.filter((u) => u.role === 'project_manager' || u.role === 'admin')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
          </select>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--color-text-muted)',
              textTransform: 'none',
              letterSpacing: 0,
            }}
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              style={{ width: 14, height: 14 }}
            />
            Archivierte zeigen
          </label>
        </div>

        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          {projects.isLoading ? (
            <div className="loading">Lade Projekte …</div>
          ) : projects.error ? (
            <div className="empty-state">
              <p>Fehler beim Laden: {apiErrorMessage(projects.error)}</p>
            </div>
          ) : (projects.data ?? []).length === 0 ? (
            <div className="empty-state">
              <p>Keine Projekte gefunden.</p>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <SortableTh sortKey="order_number" current={sortKey} dir={sortDir} onClick={toggleSort}>
                    Auftrag
                  </SortableTh>
                  <SortableTh sortKey="title" current={sortKey} dir={sortDir} onClick={toggleSort}>
                    Projekt
                  </SortableTh>
                  <th>Bauherr / Architekt</th>
                  <th>PL / BL</th>
                  <SortableTh sortKey="start_date" current={sortKey} dir={sortDir} onClick={toggleSort}>
                    Start
                  </SortableTh>
                  <SortableTh sortKey="end_date" current={sortKey} dir={sortDir} onClick={toggleSort}>
                    Ende
                  </SortableTh>
                  <SortableTh sortKey="status" current={sortKey} dir={sortDir} onClick={toggleSort}>
                    Status
                  </SortableTh>
                  <th aria-label="Aktionen" />
                </tr>
              </thead>
              <tbody>
                {(projects.data ?? []).map((p) => (
                  <tr key={p.id} className={p.is_archived ? 'archived' : undefined}>
                    <td data-label="Auftrag" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      <Link to={`/projects/${p.id}`}>{p.order_number}</Link>
                    </td>
                    <td data-label="Projekt">
                      <Link to={`/projects/${p.id}`} style={{ fontWeight: 600, color: 'var(--color-navy)' }}>
                        {p.title}
                      </Link>
                      {p.notes && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-soft)', marginTop: 2, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.notes}
                        </div>
                      )}
                    </td>
                    <td data-label="Bauherr / Arch.">
                      <div>{p.client_name ?? '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>{p.architect_name ?? ''}</div>
                    </td>
                    <td data-label="PL / BL">
                      <div>{p.project_manager_abbr ?? '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>{p.site_manager_abbr ?? ''}</div>
                    </td>
                    <td data-label="Start">{formatDate(p.start_date)}</td>
                    <td data-label="Ende">{formatDate(p.end_date)}</td>
                    <td data-label="Status">
                      <StatusBadge status={p.status} />
                    </td>
                    <td data-label="" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {canEdit && (
                        <>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditTarget(p)}>
                            Bearbeiten
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleArchiveToggle(p)}
                          >
                            {p.is_archived ? 'Reaktivieren' : 'Archivieren'}
                          </button>
                        </>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => setDeleteTarget(p)}
                        >
                          Löschen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        open={createOpen}
        title="Neues Projekt"
        onClose={() => {
          setCreateOpen(false);
          createMut.reset();
        }}
        width={720}
      >
        <ProjectForm
          isCreate
          onSubmit={handleCreate}
          onCancel={() => setCreateOpen(false)}
          submitting={createMut.isPending}
          errorMessage={createMut.isError ? apiErrorMessage(createMut.error) : null}
        />
      </Modal>

      <Modal
        open={!!editTarget}
        title={editTarget ? `Projekt bearbeiten — ${editTarget.order_number}` : ''}
        onClose={() => {
          setEditTarget(null);
          updateMut.reset();
        }}
        width={720}
      >
        {editTarget && (
          <ProjectForm
            initial={editTarget}
            onSubmit={handleUpdate}
            onCancel={() => setEditTarget(null)}
            submitting={updateMut.isPending}
            errorMessage={updateMut.isError ? apiErrorMessage(updateMut.error) : null}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Projekt löschen"
        danger
        message={
          deleteTarget
            ? `Projekt ${deleteTarget.order_number} "${deleteTarget.title}" inklusive aller Aufgaben endgültig löschen?`
            : ''
        }
        confirmLabel="Endgültig löschen"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}

interface SortableThProps {
  sortKey: SortKey;
  current: SortKey;
  dir: 'asc' | 'desc';
  onClick: (k: SortKey) => void;
  children: React.ReactNode;
}

function SortableTh({ sortKey, current, dir, onClick, children }: SortableThProps) {
  const active = sortKey === current;
  return (
    <th
      onClick={() => onClick(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title="Sortieren"
    >
      {children}
      <span style={{ marginLeft: 4, color: active ? 'var(--color-primary)' : 'var(--color-text-soft)' }}>
        {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
}
